import { AlertTriangle, Info, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { cn } from "@/lib/utils";

/**
 * Pencil component `THMrr` (`v2/Banner/Info`). Status-tinted banner with
 * icon, text, and optional action slot on the right. Used at the top of
 * the Bookings page when the verifying queue > 0, and similar one-liner
 * status callouts.
 *
 * Backed by the shadcn `<Alert>` primitive — we extend `alert.tsx`
 * with three booqly-specific variants (`info` / `warning` / `success`)
 * that paint the soft tinted backgrounds the design uses, and map
 * `error` to shadcn's built-in `destructive` variant.
 */

const TONE_TO_VARIANT = {
  info: "info",
  warning: "warning",
  success: "success",
  error: "destructive",
} as const;

const ICONS = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle2,
  error: AlertCircle,
} as const;

export function Banner({
  tone = "info",
  title,
  description,
  action,
  className,
}: {
  tone?: keyof typeof ICONS;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  const Icon = ICONS[tone];
  return (
    <Alert
      variant={TONE_TO_VARIANT[tone]}
      // Banner is an inline-row layout, not a block stack — drop the
      // svg-absolute positioning Alert applies by default and use
      // flex instead. Keeps the icon vertically centered with the
      // text + action.
      className={cn(
        "flex items-center gap-3 [&>svg]:static [&>svg]:translate-y-0 [&>svg~*]:pl-0",
        className,
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <AlertTitle className="text-sm mb-0">{title}</AlertTitle>
        {description && (
          <AlertDescription className="text-xs text-muted-foreground mt-0.5">
            {description}
          </AlertDescription>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </Alert>
  );
}
